// Selamat datang (/selamat-datang), onboarding for a new Google account
// (role "pending", plan F4 and F5). One centred column (640px, the form cap),
// no shell: nothing else opens until both steps are done.
//   progress   plain spacing   "Langkah N dari 2" (design review
//                              ui.region_gate onboarding_progress, 1.96)
//   agreement  card            the User Agreement in a scrollable block, one
//                              checkbox, Setuju (card, 0.56)
//   kind       card            Penyedia or Non-Penyedia as option rows, then
//                              the Non-Penyedia role or the Penyedia bentuk
//                              usaha (Badan Usaha, Perorangan), Simpan dan mulai
// On success the session is refreshed with the new role and the user lands
// on `next` (?next=, the page a guest's menu row or a deep link was going
// to), or the chat ("/").
//
// agreementOnly: a signed-in account (not pending) that owes a newer User
// Agreement sees the agreement card alone, with no step count and no role
// step; accepting returns it to `next`, the page it was on.
import { useEffect, useState, type FormEvent } from "react";
import * as API from "../api";
import type { NonPenyediaRole, OnboardingState, PenyediaType } from "../api";
import { useAuth, useUser } from "../auth";
import { NON_PENYEDIA_ROLES, ROLE_LABEL } from "../lib/labels";
import { getGuestState } from "../chat/guest-store";
import { PRODUCT_NAME } from "../lib/product";
import { navigate } from "../router";
import { Mark } from "../shell/Shell";
import { Notice, useTitle } from "../ui/components";
import { CheckRow, ChoiceRows, type ChoiceOption } from "./choice";

/** The Non-Penyedia roles a user may pick (and later switch between from the sidebar Mode menu). */
export const NON_PENYEDIA: NonPenyediaRole[] = [...NON_PENYEDIA_ROLES];

const ROLE_HINT: Record<NonPenyediaRole, string> = {
  pa_kpa: "Pengguna Anggaran atau Kuasa Pengguna Anggaran.",
  ppk: "Pejabat Pembuat Komitmen.",
  pokja: "Kelompok kerja pemilihan pada UKPBJ.",
  pejabat_pengadaan: "Pejabat yang melaksanakan pengadaan langsung dan penunjukan langsung.",
  tim_teknis: "Tim atau unit pengguna yang menyiapkan spesifikasi dan memeriksa hasil pekerjaan.",
  tim_swakelola: "Tim Persiapan, Tim Pelaksana, atau Tim Pengawas pekerjaan Swakelola.",
};

type Kind = "penyedia" | "non_penyedia";

/** Bentuk usaha of a Penyedia (migration 0022); also editable in Profil. */
export const PENYEDIA_TYPES: ChoiceOption<PenyediaType>[] = [
  { id: "badan_usaha", label: "Badan Usaha", hint: "PT, CV, firma, koperasi, atau badan usaha lain." },
  { id: "perorangan", label: "Perorangan", hint: "Pelaku usaha perseorangan." },
];

const KINDS: ChoiceOption<Kind>[] = [
  { id: "non_penyedia", label: "Non-Penyedia", hint: "Pejabat atau pengelola pengadaan di instansi pemerintah." },
  { id: "penyedia", label: "Penyedia", hint: "Pelaku usaha yang mengikuti pemilihan dan menyusun penawaran." },
];

function Step({ n, title, lead }: { n?: 1 | 2; title: string; lead: string }) {
  return (
    <header className="entry-head">
      {n ? <p className="entry-step">Langkah {n} dari 2</p> : null}
      <h1 className="entry-title">{title}</h1>
      <p className="entry-lead">{lead}</p>
    </header>
  );
}

export interface OnboardingProps {
  /** Only the agreement step, for an account that owes a newer version. */
  agreementOnly?: boolean;
  /** Where the user goes once done: after accepting (agreementOnly) or after choosing a role (a same-origin path). */
  next?: string;
}

export function OnboardingScreen({ agreementOnly = false, next = "/" }: OnboardingProps = {}) {
  useTitle(agreementOnly ? "Persetujuan Pengguna" : "Selamat datang");
  const user = useUser();
  const { setUser, signOut, agreementAccepted } = useAuth();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [tries, setTries] = useState(0);
  const [agreed, setAgreed] = useState(false);
  // Pre-filled from the guest chat on this device (Pluto asked the role there); the user still confirms.
  const [guestPick] = useState(() => {
    const p = getGuestState().profile;
    const sub = p?.subRole && (NON_PENYEDIA as string[]).includes(p.subRole) ? (p.subRole as NonPenyediaRole) : null;
    return p?.role ? { kind: p.role as Kind, role: p.role === "non_penyedia" ? sub : null } : null;
  });
  const [kind, setKind] = useState<Kind | null>(guestPick?.kind ?? null);
  const [role, setRole] = useState<NonPenyediaRole | null>(guestPick?.role ?? null);
  const [penyediaType, setPenyediaType] = useState<PenyediaType | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    API.getOnboarding().then(
      (s) => alive && setState(s),
      (err) => alive && setLoadError(err),
    );
    return () => {
      alive = false;
    };
  }, [tries]);

  // agreementOnly: back to the page the user was on, with no role step.
  const finish = () => {
    agreementAccepted();
    navigate(next, { replace: true });
  };

  // Accepted elsewhere (another tab) in the meantime: nothing is left to do.
  useEffect(() => {
    if (agreementOnly && state?.accepted) finish();
  }, [agreementOnly, state]);

  const accept = async (e: FormEvent) => {
    e.preventDefault();
    if (!state || busy) return;
    if (!agreed) {
      setFieldError("Centang persetujuan untuk melanjutkan.");
      document.getElementById("agree")?.focus();
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const saved = await API.acceptAgreement(state.agreementVersion);
      if (agreementOnly) {
        finish();
        return;
      }
      setState(saved);
    } catch (err) {
      if (err instanceof API.ApiError && err.status === 422) {
        setFailure("Teks persetujuan baru saja diperbarui. Baca versi terbaru di bawah, lalu setujui lagi.");
        setAgreed(false);
        setTries((t) => t + 1);
      } else setFailure(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const choose = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!kind) {
      setFieldError("Pilih jenis akun.");
      return;
    }
    if (kind === "non_penyedia" && !role) {
      setFieldError("Pilih peran Anda.");
      return;
    }
    if (kind === "penyedia" && !penyediaType) {
      setFieldError("Pilih bentuk usaha Anda.");
      return;
    }
    setBusy(true);
    setFailure(null);
    setFieldError(null);
    try {
      const chosen = await API.chooseRole(kind === "penyedia" ? { kind: "penyedia", penyediaType: penyediaType! } : { kind: "non_penyedia", role: role! });
      setUser(chosen);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof API.ApiError && err.status === 409) {
        // The agreement is missing, or the role was already chosen elsewhere.
        setFailure(err.message);
        setTries((t) => t + 1);
      } else setFailure(API.errorMessage(err));
      setBusy(false);
    }
  };

  let body;
  if (loadError) {
    body = (
      <Notice
        tone="danger"
        title="Data persetujuan belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={() => setTries((t) => t + 1)}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(loadError)}</p>
      </Notice>
    );
  } else if (!state) {
    body = (
      <p className="entry-note" role="status">
        Memuat persetujuan pengguna
      </p>
    );
  } else if (!state.accepted || agreementOnly) {
    body = (
      <>
        {agreementOnly ? (
          <Step title="Persetujuan Pengguna diperbarui" lead={`Baca dan setujui versi terbaru untuk terus memakai ${PRODUCT_NAME}. Setelah itu Anda kembali ke halaman sebelumnya.`} />
        ) : (
          <Step n={1} title="Persetujuan Pengguna" lead={`Baca dan setujui ketentuan penggunaan ${PRODUCT_NAME} sebelum mulai.`} />
        )}
        <form className="card entry-card" noValidate aria-label="Persetujuan Pengguna" onSubmit={accept}>
          {failure ? (
            <Notice tone="danger" title="Persetujuan belum tersimpan">
              <p>{failure}</p>
            </Notice>
          ) : null}
          <div className="agreement" tabIndex={0} role="region" aria-label="Teks Persetujuan Pengguna">
            {state.agreementText
              .split(/\n\s*\n/)
              .map((p) => p.trim())
              .filter(Boolean)
              .map((p, i) => (
                <p key={i}>{p}</p>
              ))}
          </div>
          <div className="entry-check">
            <CheckRow
              id="agree"
              checked={agreed}
              invalid={fieldError !== null}
              describedBy="agree-hint"
              onChange={(next) => {
                setAgreed(next);
                setFieldError(null);
              }}
            >
              Saya sudah membaca dan menyetujui Persetujuan Pengguna.
            </CheckRow>
            <p id="agree-hint" className="field-hint" data-state={fieldError ? "error" : undefined} aria-live="polite">
              {fieldError ?? ""}
            </p>
          </div>
          <div className="button-row">
            <button type="submit" className="btn" disabled={!agreed || busy} aria-busy={busy || undefined}>
              {busy ? "Menyimpan" : "Setuju"}
            </button>
          </div>
        </form>
      </>
    );
  } else {
    body = (
      <>
        <Step n={2} title="Pilih jenis akun" lead="Menu dan dokumen menyesuaikan jenis akun Anda. Peran Non-Penyedia dan bentuk usaha Penyedia dapat diganti nanti dari menu samping; jenis akun tidak." />
        <form className="card entry-card" noValidate aria-label="Jenis akun" onSubmit={choose}>
          {failure ? (
            <Notice tone="danger" title="Jenis akun belum tersimpan">
              <p>{failure}</p>
            </Notice>
          ) : null}
          <div className="entry-group">
            <h2 id="kind-title" className="entry-legend">
              Jenis akun
            </h2>
            <ChoiceRows
              labelledBy="kind-title"
              idBase="kind"
              options={KINDS}
              value={kind}
              onChange={(next) => {
                setKind(next);
                setFieldError(null);
              }}
            />
            {guestPick ? <p className="entry-prefill">Dipilih dari percakapan tamu Anda. Ubah bila perlu, lalu simpan.</p> : null}
          </div>
          {kind === "non_penyedia" ? (
            <div className="entry-group">
              <h2 id="role-title" className="entry-legend">
                Peran Anda
              </h2>
              <ChoiceRows
                labelledBy="role-title"
                idBase="role"
                options={NON_PENYEDIA.map((r) => ({ id: r, label: ROLE_LABEL[r], hint: ROLE_HINT[r] }))}
                value={role}
                onChange={(next) => {
                  setRole(next);
                  setFieldError(null);
                }}
              />
            </div>
          ) : null}
          {kind === "penyedia" ? (
            <div className="entry-group">
              <h2 id="ptype-title" className="entry-legend">
                Bentuk usaha
              </h2>
              <ChoiceRows
                labelledBy="ptype-title"
                idBase="ptype"
                options={PENYEDIA_TYPES}
                value={penyediaType}
                onChange={(next) => {
                  setPenyediaType(next);
                  setFieldError(null);
                }}
              />
            </div>
          ) : null}
          {kind ? (
            <p className="field-hint onboarding-note">
              {kind === "penyedia"
                ? "Bentuk usaha dapat diubah lagi nanti dari menu Mode di samping atau halaman Profil. Jenis akun tidak dapat diubah setelah disimpan. Akun Penyedia tidak dapat pindah ke Non-Penyedia tanpa bantuan Administrator."
                : "Peran dapat diubah lagi nanti dari menu Mode di samping atau halaman Profil. Jenis akun tidak dapat diubah setelah disimpan. Akun Non-Penyedia tidak dapat pindah ke Penyedia tanpa bantuan Administrator."}
            </p>
          ) : null}
          {/* Always rendered, so the live region exists before an error is announced. */}
          <p className="field-hint" data-state={fieldError ? "error" : undefined} aria-live="polite" hidden={!fieldError}>
            {fieldError ?? ""}
          </p>
          <div className="button-row">
            <button type="submit" className="btn" aria-busy={busy || undefined}>
              {busy ? "Menyimpan" : "Simpan dan mulai"}
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <main id="main" className="entry" data-ui-shell="none" data-jal-shell="none">
      <div className="entry-column">
        <div className="entry-brand">
          <Mark />
          <span className="entry-brand-name">{PRODUCT_NAME}</span>
        </div>
        {body}
        <div className="entry-foot">
          <p className="entry-foot-text">Masuk sebagai {user.email}</p>
          <button type="button" className="btn btn-ghost" onClick={() => void signOut()}>
            Keluar
          </button>
        </div>
      </div>
    </main>
  );
}
